/**
 * /r/:code — affiliate referral capture. Drops a first-touch `dunea_ref` cookie,
 * pings the click endpoint, and redirects home. Public (no auth); renders nothing.
 */
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { apiTrackAffiliateClick } from '~/lib/api/affiliates';
import { setAffiliateRefIfAbsent } from '~/lib/affiliateRef';
import { capture } from '~/lib/posthog';

export default function AffiliateCapturePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      setAffiliateRefIfAbsent(code);
      // Best-effort — never block the redirect on the tracking call.
      apiTrackAffiliateClick(code, `/r/${code}`).catch(() => {});

      /*
       * Fired here rather than from the public /affiliates/track endpoint on
       * purpose. The click is anonymous, and the server has no PostHog identity
       * for a visitor who has not signed up — it would have to attribute the
       * click to the AFFILIATE, which measures the link but breaks the funnel.
       * From the browser the event lands on the visitor's own distinct_id, so
       * when they later register the alias joins the click to the signup and
       * the referral becomes one continuous funnel.
       */
      capture(ANALYTICS_EVENTS.AFFILIATE_LINK_CLICKED, { code, landing: `/r/${code}` });
    }

    navigate('/', { replace: true });
  }, [code, navigate]);

  return null;
}
